import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { marketMoversService } from '../services/marketMoversService';
import type { MarketMover } from '../types/marketMovers';
import { TZ } from '../utils/marketTime';

interface MarketMoversWidgetProps {
  onRemove: () => void;
}

type TabView = 'gainers-losers' | 'active';

const formatPrice = (price: number): string => `$${price.toFixed(2)}`;

const formatChange = (change: number): string => {
  const sign = change >= 0 ? '+' : '';
  return `${sign}$${Math.abs(change).toFixed(2)}`;
};

const formatPct = (pct: number): string => {
  const sign = pct >= 0 ? '+' : '';
  return `${sign}${pct.toFixed(2)}%`;
};

const formatUpdated = (isoStr: string): string => {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  return d.toLocaleTimeString('en-US', { timeZone: TZ, hour: 'numeric', minute: '2-digit' });
};

const getChangeStyle = (change: number): string =>
  change >= 0 ? 'text-bull-blue' : 'text-bear-red';

const TickerLink = ({ symbol }: { symbol: string }) => (
  <a
    href={`/ticker/${symbol}`}
    target="_blank"
    rel="noopener noreferrer"
    className="font-medium text-white hover:text-bull-blue transition-colors"
  >
    {symbol}
  </a>
);

// Compact panel for side-by-side gainers/losers
interface MoversPanelProps {
  title: string;
  accentColor: 'bull-blue' | 'bear-red';
  data: MarketMover[] | undefined;
  isLoading: boolean;
  error: Error | null;
}

function MoversPanel({ title, accentColor, data, isLoading, error }: MoversPanelProps) {
  return (
    <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
      <div className={`px-3 py-1.5 border-b border-dark-border bg-${accentColor}/10`}>
        <h4 className={`text-xs font-semibold text-${accentColor}`}>{title}</h4>
      </div>
      <div className="flex-1 overflow-auto">
        {isLoading && (
          <div className="flex items-center justify-center h-full text-gray-400 text-sm">
            Loading...
          </div>
        )}
        {error && (
          <div className="flex items-center justify-center h-full text-bear-red text-sm p-2">
            Error loading
          </div>
        )}
        {data && data.length > 0 && (
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-dark-surface-light border-b border-dark-border">
              <tr>
                <th className="px-2 py-1.5 text-left font-semibold text-gray-300">Symbol</th>
                <th className="px-2 py-1.5 text-right font-semibold text-gray-300">Price</th>
                <th className="px-2 py-1.5 text-right font-semibold text-gray-300">% Chg</th>
              </tr>
            </thead>
            <tbody>
              {data.map((mover: MarketMover) => (
                <tr
                  key={mover.symbol}
                  className="border-b border-dark-border hover:bg-dark-surface-light transition-colors"
                >
                  <td className="px-2 py-1.5">
                    <TickerLink symbol={mover.symbol} />
                  </td>
                  <td className="px-2 py-1.5 text-right font-numeric text-white">
                    {formatPrice(mover.price)}
                  </td>
                  <td className={`px-2 py-1.5 text-right ${getChangeStyle(mover.changesPercentage)}`}>
                    {formatPct(mover.changesPercentage)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {data && data.length === 0 && (
          <div className="flex items-center justify-center h-full text-gray-500 text-xs">
            No data available
          </div>
        )}
      </div>
    </div>
  );
}

export function MarketMoversWidget({ onRemove }: MarketMoversWidgetProps) {
  const [activeTab, setActiveTab] = useState<TabView>('gainers-losers');

  const queryOpts = {
    staleTime: 3 * 60 * 1000,
    refetchInterval: 3 * 60 * 1000,
    refetchOnWindowFocus: true,
  };

  const gainersQuery = useQuery({
    queryKey: ['marketMovers', 'gainers'],
    queryFn: () => marketMoversService.fetchMovers('gainers'),
    ...queryOpts,
  });

  const losersQuery = useQuery({
    queryKey: ['marketMovers', 'losers'],
    queryFn: () => marketMoversService.fetchMovers('losers'),
    ...queryOpts,
  });

  const activeQuery = useQuery({
    queryKey: ['marketMovers', 'active'],
    queryFn: () => marketMoversService.fetchMovers('active'),
    enabled: activeTab === 'active',
    ...queryOpts,
  });

  // Show updated time from whichever query has data
  const updatedAt =
    gainersQuery.data?.meta?.updated_at
      ? formatUpdated(gainersQuery.data.meta.updated_at)
      : activeQuery.data?.meta?.updated_at
        ? formatUpdated(activeQuery.data.meta.updated_at)
        : '';

  return (
    <div className="h-full flex flex-col bg-dark-surface rounded-lg overflow-hidden">
      {/* Title Bar - drag handle */}
      <div className="widget-drag-handle flex items-center px-3 py-1.5 bg-chrome border-b border-dark-border cursor-move relative z-20">
        <h3 className="text-sm font-bold text-white uppercase tracking-[1.5px]">Market Movers</h3>
        {updatedAt && (
          <span className="text-xs text-gray-500">Updated {updatedAt}</span>
        )}
      </div>

      {/* Toolbar with tabs and close button */}
      <div className="flex items-center justify-between px-2 py-1 bg-dark-bg/50 border-b border-dark-border relative z-20">
        <div className="flex gap-1">
          <button
            onClick={() => setActiveTab('gainers-losers')}
            className={`px-3 py-0.5 text-xs rounded-sm transition-colors ${
              activeTab === 'gainers-losers'
                ? 'bg-dark-surface-light text-white border-b-2 border-accent-yellow'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Gainers & Losers
          </button>
          <button
            onClick={() => setActiveTab('active')}
            className={`px-3 py-0.5 text-xs rounded-sm transition-colors ${
              activeTab === 'active'
                ? 'bg-dark-surface-light text-white border-b-2 border-accent-yellow'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Active
          </button>
        </div>
        <button
          onClick={onRemove}
          className="text-gray-400 hover:text-white hover:bg-bear-red/20 text-sm px-2 py-0.5 rounded transition-colors"
          title="Close widget"
        >
          ×
        </button>
      </div>

      {/* Content */}
      {activeTab === 'gainers-losers' ? (
        <div className="flex-1 flex min-h-0 overflow-hidden">
          <MoversPanel
            title="Gainers"
            accentColor="bull-blue"
            data={gainersQuery.data?.data}
            isLoading={gainersQuery.isLoading}
            error={gainersQuery.error}
          />
          <div className="w-px bg-dark-border" />
          <MoversPanel
            title="Losers"
            accentColor="bear-red"
            data={losersQuery.data?.data}
            isLoading={losersQuery.isLoading}
            error={losersQuery.error}
          />
        </div>
      ) : (
        <div className="flex-1 overflow-auto">
          {activeQuery.isLoading && (
            <div className="flex items-center justify-center h-full text-gray-400 text-sm">
              Loading...
            </div>
          )}
          {activeQuery.error && (
            <div className="flex items-center justify-center h-full text-bear-red text-sm">
              Error loading data
            </div>
          )}
          {activeQuery.data && activeQuery.data.data.length > 0 && (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-dark-surface-light border-b border-dark-border">
                <tr>
                  <th className="px-2 py-1.5 text-left font-semibold text-gray-300">Symbol</th>
                  <th className="px-2 py-1.5 text-left font-semibold text-gray-300">Name</th>
                  <th className="px-2 py-1.5 text-right font-semibold text-gray-300">Price</th>
                  <th className="px-2 py-1.5 text-right font-semibold text-gray-300">Change</th>
                  <th className="px-2 py-1.5 text-right font-semibold text-gray-300">% Change</th>
                </tr>
              </thead>
              <tbody>
                {activeQuery.data.data.map((mover: MarketMover) => (
                  <tr
                    key={mover.symbol}
                    className="border-b border-dark-border hover:bg-dark-surface-light transition-colors"
                  >
                    <td className="px-2 py-1.5">
                      <TickerLink symbol={mover.symbol} />
                    </td>
                    <td
                      className="px-2 py-1.5 text-white truncate max-w-[140px]"
                      title={mover.name}
                    >
                      {mover.name}
                    </td>
                    <td className="px-2 py-1.5 text-right font-numeric text-white">
                      {formatPrice(mover.price)}
                    </td>
                    <td className={`px-2 py-1.5 text-right ${getChangeStyle(mover.change)}`}>
                      {formatChange(mover.change)}
                    </td>
                    <td className={`px-2 py-1.5 text-right ${getChangeStyle(mover.changesPercentage)}`}>
                      {formatPct(mover.changesPercentage)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {activeQuery.data && activeQuery.data.data.length === 0 && (
            <div className="flex items-center justify-center h-full text-gray-500 text-sm">
              No data available
            </div>
          )}
        </div>
      )}
    </div>
  );
}
